/**
 * Recorded chunks are written to IndexedDB the moment MediaRecorder hands them
 * over, and removed once the server has them. A reload, a crash or a closed tab
 * therefore leaves recoverable audio behind rather than nothing.
 */
const DB_NAME = 'sl-meetings-chunks';
const DB_VERSION = 1;
const STORE = 'chunks';
const BY_MEETING = 'byMeeting';

export interface StoredChunk {
  key: string;
  meetingId: string;
  attempt: number;
  index: number;
  blob: Blob;
  mimeType: string;
  savedAt: number;
}

export function chunkKey(meetingId: string, attempt: number, index: number): string {
  return `${meetingId}/${attempt}/${index}`;
}

function openDb(): Promise<IDBDatabase | null> {
  return new Promise((resolve) => {
    if (typeof indexedDB === 'undefined') {
      resolve(null);
      return;
    }
    let request: IDBOpenDBRequest;
    try {
      request = indexedDB.open(DB_NAME, DB_VERSION);
    } catch {
      resolve(null);
      return;
    }
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE)) {
        const store = db.createObjectStore(STORE, { keyPath: 'key' });
        store.createIndex(BY_MEETING, 'meetingId', { unique: false });
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => resolve(null);
    request.onblocked = () => resolve(null);
  });
}

async function withStore<T>(
  mode: IDBTransactionMode,
  fn: (store: IDBObjectStore) => IDBRequest,
  fallback: T,
): Promise<T> {
  const db = await openDb();
  if (!db) return fallback;
  return new Promise<T>((resolve) => {
    try {
      const tx = db.transaction(STORE, mode);
      const request = fn(tx.objectStore(STORE));
      request.onsuccess = () => resolve(request.result as T);
      request.onerror = () => resolve(fallback);
      tx.onabort = () => resolve(fallback);
    } catch {
      resolve(fallback);
    } finally {
      // The connection is cheap to reopen; holding it blocks version changes.
      setTimeout(() => db.close(), 0);
    }
  });
}

export async function saveChunk(chunk: Omit<StoredChunk, 'key' | 'savedAt'>): Promise<void> {
  const record: StoredChunk = {
    ...chunk,
    key: chunkKey(chunk.meetingId, chunk.attempt, chunk.index),
    savedAt: Date.now(),
  };
  await withStore<unknown>('readwrite', (store) => store.put(record), null);
}

export async function deleteChunk(
  meetingId: string,
  attempt: number,
  index: number,
): Promise<void> {
  await withStore<unknown>(
    'readwrite',
    (store) => store.delete(chunkKey(meetingId, attempt, index)),
    null,
  );
}

export async function listChunks(meetingId: string): Promise<StoredChunk[]> {
  const rows = await withStore<StoredChunk[]>(
    'readonly',
    (store) => store.index(BY_MEETING).getAll(meetingId),
    [],
  );
  return [...(rows ?? [])].sort((a, b) => a.attempt - b.attempt || a.index - b.index);
}

/** Chunks of the newest attempt still waiting to be uploaded, if any. */
export async function pendingUpload(
  meetingId: string,
): Promise<{ attempt: number; chunks: StoredChunk[] } | null> {
  const all = await listChunks(meetingId);
  if (all.length === 0) return null;
  const attempt = all[all.length - 1]!.attempt;
  return { attempt, chunks: all.filter((c) => c.attempt === attempt) };
}

export async function clearMeeting(meetingId: string): Promise<void> {
  const rows = await listChunks(meetingId);
  for (const row of rows) await deleteChunk(row.meetingId, row.attempt, row.index);
}
